import { useState, useCallback } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useAppSelector } from 'store/hooks';

export interface UseApiErrorReturnType {
  showErrorModal: boolean;
  handleApiError: (
    error:
      | FetchBaseQueryError
      | SerializedError
      | { status: string; data: string }
      | undefined
  ) => void;
  handleRetry: () => void;
  hideErrorModal: () => void;
}

export const useApiError = (
  retryCallback: () => void
): UseApiErrorReturnType => {
  const [showErrorModal, setShowErrorModal] = useState(false);
  // Determine if we are currently generating a report; suppress certain error modals during BUSY state
  const isReportGenerating = useAppSelector(
    state =>
      state.project.selectedProject?.reports?.[0]?.status === 'BUSY' ||
      state.project.selectedProject?.reports?.[0] === undefined
  );

  const handleApiError = useCallback(
    (
      error:
        | FetchBaseQueryError
        | SerializedError
        | { status: string; data: string }
        | undefined
    ) => {
      if (error) {
        // Handle custom authentication error - don't show modal, just let redirect happen
        if ('status' in error && error.status === 'AUTHENTICATION_ERROR') {
          return; // Don't show error modal for auth redirects
        }

        // If a report is actively generating, suppress user-facing modals for transient errors
        // Allow: 400, 404, 422, 429, generic fetch/timeouts, and 5xx to pass silently (auto-reties handled elsewhere)
        if (isReportGenerating) {
          // Determine numeric status (prefer direct status, else originalStatus)
          const numericStatus =
            typeof (error as any).status === 'number'
              ? (error as any).status
              : (error as any).originalStatus;
          // Suppress all non-auth HTTP errors while BUSY
          if (
            typeof numericStatus === 'number' &&
            numericStatus >= 400 &&
            numericStatus < 600 &&
            numericStatus !== 401 &&
            numericStatus !== 403
          ) {
            return; // Ignore modal display
          }
          // Suppress RTK string status transient network issues
          if (
            typeof (error as any).status === 'string' &&
            ['FETCH_ERROR', 'TIMEOUT_ERROR', 'PARSING_ERROR'].includes(
              (error as any).status
            )
          ) {
            return;
          }
        }

        // Show error modal based on status codes
        if ('status' in error) {
          // Handle numeric HTTP status codes
          if (typeof error.status === 'number') {
            const status = error.status;

            // Handle client errors (4xx) - show modal for most except auth errors
            if (status >= 400 && status < 500) {
              // 404 Not Found - show modal
              if (status === 404) {
                setShowErrorModal(true);
                return;
              }

              // 400, 422, etc. - show modal for bad requests
              if (status === 400 || status === 422 || status === 429) {
                setShowErrorModal(true);
                return;
              }

              // Auth errors (401, 403) are handled in baseQueryWithAuth
              // and converted to AUTHENTICATION_ERROR, so they won't reach here
            }

            // Handle server errors (5xx) - always show modal
            if (status >= 500) {
              setShowErrorModal(true);
              return;
            }
          }

          // Handle string status codes (RTK Query specific)
          if (typeof error.status === 'string') {
            if (
              error.status === 'FETCH_ERROR' ||
              error.status === 'TIMEOUT_ERROR' ||
              error.status === 'PARSING_ERROR'
            ) {
              setShowErrorModal(true);
              return;
            }
          }
        }

        // Check for originalStatus in parsing errors and other RTK Query errors
        if (
          'originalStatus' in error &&
          typeof error.originalStatus === 'number'
        ) {
          const originalStatus = error.originalStatus;

          // Handle client errors (4xx) based on originalStatus
          if (originalStatus >= 400 && originalStatus < 500) {
            // 404 Not Found - show modal
            if (originalStatus === 404) {
              setShowErrorModal(true);
              return;
            }

            // 400, 422, etc. - show modal for bad requests
            if (
              originalStatus === 400 ||
              originalStatus === 422 ||
              originalStatus === 429
            ) {
              setShowErrorModal(true);
              return;
            }

            // Don't show modal for auth errors (they should be handled by baseQueryWithAuth)
            if (originalStatus === 401 || originalStatus === 403) {
              return;
            }
          }

          // Handle server errors (5xx) based on originalStatus - always show modal
          if (originalStatus >= 500) {
            setShowErrorModal(true);
            return;
          }
        }

        // For SerializedError or other unknown errors without 'status' property
        if (!('status' in error)) {
          setShowErrorModal(true);
        }
      }
    },
    [isReportGenerating]
  );

  const handleRetry = useCallback(() => {
    setShowErrorModal(false);
    retryCallback();
  }, [retryCallback]);

  const hideErrorModal = useCallback(() => {
    setShowErrorModal(false);
  }, []);

  return {
    showErrorModal,
    handleApiError,
    handleRetry,
    hideErrorModal,
  };
};
